<template>
  <div class="h-full relative">
    <DialogHeader
      :title="useT('cliToken.title')"
      size="large"
      show-close
      class="absolute top-0 left-0 z-20 bg-white w-full rounded-tr-[var(--el-dialog-border-radius)]"
      @close="useDialogStore().close('settings')"
    />
    <div class="h-full overflow-auto">
      <div class="py-16">
        <div class="flex justify-between items-center gap-6 p-6">
          <p class="text-sm text-gray-500 max-w-xl leading-snug">
            {{ useT('cliToken.description') }}
          </p>
          <Button
            type="primary"
            icon-left="plus"
            @click="openCreate"
          >
            {{ useT('cliToken.new') }}
          </Button>
        </div>

        <div class="px-6 pb-6 w-full">
          <el-table
            v-if="!isLoading && !loadFailed && tokens.length"
            :data="tokensSorted"
            row-key="_id"
            :row-class-name="rowClassName"
          >
            <el-table-column
              prop="label"
              :label="useT('cliToken.column.label')"
              min-width="180"
            >
              <template #default="scope">
                <div class="text-sm font-medium text-gray-900 truncate">
                  {{ scope.row.label }}
                </div>
                <el-tooltip
                  :content="formatDate(scope.row.createdAt)"
                  placement="top"
                  :hide-after="0"
                >
                  <span class="text-xs text-gray-500">
                    {{ useT('cliToken.createdAt', { time: formatRelative(scope.row.createdAt) }) }}
                  </span>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column
              :label="useT('cliToken.column.scopes')"
              width="200"
            >
              <template #default="scope">
                <div class="flex flex-wrap gap-1">
                  <el-tooltip
                    v-for="tokenScope in orderedScopes(scope.row.scope)"
                    :key="tokenScope"
                    :content="scopeTooltip(tokenScope)"
                    placement="top"
                    :hide-after="0"
                  >
                    <Badge
                      :text="scopeLabel(tokenScope)"
                      :type="scopeBadgeType(tokenScope)"
                    />
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              :label="useT('cliToken.column.status')"
              width="150"
            >
              <template #default="scope">
                <Badge
                  :text="statusLabel(scope.row)"
                  :type="statusType(scope.row)"
                />
                <el-tooltip
                  :content="formatDate(statusDate(scope.row))"
                  placement="top"
                  :hide-after="0"
                >
                  <span
                    class="block text-xs mt-1"
                    :class="isExpiringSoon(scope.row) ? 'text-yellow-800 font-medium' : 'text-gray-500'"
                  >
                    {{ statusDetail(scope.row) }}
                  </span>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column
              :label="useT('cliToken.column.lastUsed')"
              width="130"
            >
              <template #default="scope">
                <el-tooltip
                  v-if="scope.row.lastUsedAt"
                  :content="formatDate(scope.row.lastUsedAt)"
                  placement="top"
                  :hide-after="0"
                >
                  <span class="text-xs text-gray-500">
                    {{ formatRelative(scope.row.lastUsedAt) }}
                  </span>
                </el-tooltip>
                <span
                  v-else
                  class="text-xs text-gray-400"
                >
                  {{ useT('cliToken.never') }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              label=""
              width="96"
              align="right"
            >
              <template #default="scope">
                <div
                  v-if="!isInactive(scope.row)"
                  class="flex justify-end gap-1"
                >
                  <Button
                    type="regular-text"
                    size="small"
                    content-icon="pencil-01"
                    :tooltip-text="useT('cliToken.edit')"
                    @click.stop="openEdit(scope.row)"
                  />
                  <Button
                    type="danger-text"
                    size="small"
                    content-icon="trash-01"
                    :tooltip-text="useT('cliToken.revoke')"
                    @click.stop="revoke(scope.row)"
                  />
                </div>
              </template>
            </el-table-column>
          </el-table>
          <div
            v-show="isLoading"
            v-loading="isLoading"
            class="w-full h-48"
          />
          <div
            v-if="tokens.length && !isLoading && !loadFailed"
            class="el-table__counter"
          >
            {{ useT('bcd4', { n: tokens.length }) }}
          </div>
          <EmptyState
            v-if="loadFailed && !isLoading"
            icon="alert-triangle"
            :title="useT('cliToken.loadError.title')"
            :description="useT('cliToken.loadError')"
          >
            <Button
              type="regular-outline"
              icon-left="refresh-cw-01"
              @click="fetchTokens"
            >
              {{ useT('cliToken.loadError.retry') }}
            </Button>
          </EmptyState>
          <EmptyState
            v-else-if="!tokens.length && !isLoading"
            icon="key-01"
            :title="useT('cliToken.empty.title')"
            :description="useT('cliToken.empty')"
          >
            <Button
              type="primary"
              icon-left="plus"
              @click="openCreate"
            >
              {{ useT('cliToken.new') }}
            </Button>
          </EmptyState>
        </div>
      </div>
    </div>

    <Dialog
      v-if="revokeOpen"
      v-model="revokeOpen"
      size="small-hug"
      persistent
    >
      <template #header="{ close }">
        <DialogHeader
          :title="useT('cliToken.revoke.title')"
          show-close
          @close="onRevokeClose(close)"
        />
      </template>

      <p class="text-sm text-gray-700">
        {{ useT('cliToken.revoke.body', { label: revokeTarget?.label || '' }) }}
      </p>

      <template #footer>
        <ButtonGroup
          type="danger"
          align="right"
          :text-submit="useT('cliToken.revoke.confirm')"
          :text-cancel="useT('bcd205')"
          :loading="isRevoking"
          @submit="confirmRevoke"
          @cancel="onRevokeClose()"
        />
      </template>
    </Dialog>

    <Dialog
      v-if="formOpen"
      v-model="formOpen"
      size="small-hug"
      persistent
    >
      <template #header="{ close }">
        <DialogHeader
          :title="dialogTitle"
          show-close
          @close="onFormClose(close)"
        />
      </template>

      <Form
        v-if="!minted"
        ref="formRef"
        :model="form"
        :rules="rules"
        @submit="submit"
      >
        <InputBlock
          :label="useT('cliToken.column.label')"
          prop="label"
        >
          <TextInput
            v-model="form.label"
            :placeholder="useT('cliToken.create.placeholder')"
            autocomplete="off"
            name="cli-token-label"
            :maxlength="60"
          />
        </InputBlock>
        <InputBlock :label="useT('cliToken.create.scopes')">
          <CheckboxGroup
            :model-value="form.scopes"
            :options="scopeOptions"
            @update:model-value="onScopesChange"
          />
        </InputBlock>
        <InputBlock
          v-if="!isEditing"
          :label="useT('cliToken.create.expiry')"
        >
          <Select
            :model-value="form.ttlDays"
            :options="ttlOptions"
            @update:model-value="(value) => form.ttlDays = value"
          />
        </InputBlock>
        <div
          v-else
          class="text-xs text-gray-500"
        >
          {{ useT('cliToken.edit.expiryNote') }}
        </div>
      </Form>

      <div
        v-else
        class="space-y-3"
      >
        <p class="text-sm text-gray-700 font-semibold">
          {{ useT('cliToken.created.warning') }}
        </p>
        <div class="flex items-stretch gap-2">
          <pre class="flex-1 bg-gray-900 text-gray-100 text-xs px-3 py-2 rounded font-mono overflow-auto">{{ minted.token }}</pre>
          <Button
            type="regular-outline"
            :icon-left="copied ? 'check' : 'clipboard'"
            @click="copyMinted"
          >
            {{ copied ? useT('cliToken.copy.copied') : useT('cliToken.copy.label') }}
          </Button>
        </div>
        <div class="text-xs text-gray-500">
          {{ useT('cliToken.created.pasteHint') }}
        </div>
      </div>

      <template #footer>
        <ButtonGroup
          v-if="!minted"
          type="primary"
          align="right"
          :text-submit="isEditing ? useT('cliToken.edit.submit') : useT('cliToken.create.submit')"
          :text-cancel="useT('bcd205')"
          :loading="isSaving"
          @submit="formRef?.onSubmit()"
          @cancel="onFormClose()"
        />
        <div
          v-else
          class="flex justify-end"
        >
          <Button
            type="primary"
            @click="onFormClose()"
          >
            {{ useT('bcd160') }}
          </Button>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup>
// One entry per scope drives the create form's checkboxes, the table badges and
// their colours, so adding a scope is a line here plus its locale keys. Scopes
// are stored as raw ids (`traces:read`), kept in the tooltip for whoever is
// debugging a 403.
const SCOPES = [
  {
    id: 'traces:read',
    short: 'cliToken.scope.tracesRead.short',
    full: 'cliToken.scope.tracesRead',
    hint: null,
    badgeType: 'neutral',
    // Trace debugging is what most tokens are for, so it starts ticked.
    default: true,
  },
  {
    id: 'db:read',
    short: 'cliToken.scope.dbRead.short',
    full: 'cliToken.scope.dbRead',
    hint: 'cliToken.scope.dbRead.hint',
    badgeType: 'info',
    // Opt-in: a token minted for trace debugging has no reason to also reach
    // workspace collections.
    default: false,
  },
  {
    id: 'db:identity',
    short: 'cliToken.scope.dbIdentity.short',
    full: 'cliToken.scope.dbIdentity',
    hint: 'cliToken.scope.dbIdentity.hint',
    // The only scope that lets a token name a real person, so it is the only
    // one whose badge is meant to stand out in the table.
    badgeType: 'warning',
    default: false,
    // A capability, not a grant: on its own it reaches no route at all, and the
    // API rejects the pair being minted without this. Ticking it here ticks the
    // prerequisite too, so the form cannot build a combination the API refuses.
    requires: 'db:read',
  },
];

const SCOPE_BY_ID = Object.fromEntries(SCOPES.map((scope) => [scope.id, scope]));

const defaultScopes = () => SCOPES.filter((scope) => scope.default).map((scope) => scope.id);
// Expiry is the usual reason a working CLI suddenly starts failing, so a token
// inside this window gets a warning badge instead of a plain "Active".
const EXPIRING_SOON_MS = 7 * 24 * 60 * 60 * 1000;

// The API accepts any TTL from 1 to 365 days; these are the ones worth a click.
// 30 stays the default — long enough not to be a chore, short enough that a
// forgotten token on a lost laptop stops working on its own.
const TTL_DAYS = [7, 30, 90, 180, 365];
const DEFAULT_TTL_DAYS = 30;

// Refs
const tokens = ref([]);
const isLoading = ref(false);
const loadFailed = ref(false);
const formOpen = ref(false);
const isSaving = ref(false);
// The token being edited, and the flag for which mode the one dialog is in.
const editTarget = ref(null);
const minted = ref(null);
const copied = ref(false);
const formRef = ref(null);
const form = ref({ label: '', scopes: defaultScopes(), ttlDays: DEFAULT_TTL_DAYS });
const revokeOpen = ref(false);
const revokeTarget = ref(null);
const isRevoking = ref(false);

const rules = {
  label: [useFormRules().required],
};

// Computed
// Inactive tokens keep their row for audit context but sink below the live
// ones. Sort is stable, so each group stays newest-first as the API returned it.
const tokensSorted = computed(() => [...tokens.value]
  .sort((first, second) => Number(isInactive(first)) - Number(isInactive(second))));

const scopeOptions = computed(() => SCOPES.map((scope) => ({
  id: scope.id,
  text: useT(scope.full),
  description: scope.hint ? useT(scope.hint) : '',
})));

const isEditing = computed(() => Boolean(editTarget.value));

const dialogTitle = computed(() => {
  if (minted.value) { return useT('cliToken.created.title') }
  return isEditing.value ? useT('cliToken.edit.title') : useT('cliToken.create.title');
});

const ttlOptions = computed(() => TTL_DAYS.map((days) => ({
  id: days,
  text: days === 365 ? useT('cliToken.expiry.year') : useT('cliToken.expiry.days', { n: days }),
})));

// Methods
// The API's error handler sets `ctx.body = error.message`, so `error.data` is a
// plain string, not an object — without this the specific 400s ("unknown
// scope: x") would never reach anybody.
const apiErrorMessage = (error, fallbackKey) => {
  if (typeof error?.data === 'string' && error.data) { return error.data }
  return error?.data?.message || useT(fallbackKey);
};

const fetchTokens = async() => {
  isLoading.value = true;
  try {
    const response = await useApi.get('/cli-tokens');
    tokens.value = response.results || [];
    loadFailed.value = false;
  } catch (error) {
    // Without this the empty state would claim "No CLI tokens yet" to somebody
    // whose list merely failed to load.
    loadFailed.value = true;
    useToast.error(apiErrorMessage(error, 'cliToken.toast.loadError'));
  } finally {
    isLoading.value = false;
  }
};

// One dialog, two modes: `token` is the one being edited, or null for a new one.
// The form is filled HERE rather than from the dialog's own open event, because
// the dialog is mounted by `v-if` already open and so never fires one.
const openForm = (token = null) => {
  editTarget.value = token;
  // Editing starts from the token's current values, so the checkboxes show what
  // it can do today rather than what a new token would get.
  form.value = {
    label: token ? token.label : '',
    scopes: token ? orderedScopes(token.scope) : defaultScopes(),
    ttlDays: DEFAULT_TTL_DAYS,
  };
  minted.value = null;
  copied.value = false;
  formOpen.value = true;
};

const openCreate = () => openForm();

const openEdit = (token) => openForm(token);

const onFormClose = (closeFn) => {
  const wasMinted = Boolean(minted.value);
  formOpen.value = false;
  editTarget.value = null;
  if (typeof closeFn === 'function') { closeFn() }
  if (wasMinted) { fetchTokens() }
};

// Ticking a capability scope pulls in the grant it widens. Left to itself the
// form would happily submit `['db:identity']`, which the API rejects because
// that token would reach no route at all.
// A capability scope and the grant it widens move together, in both directions.
//
// Which direction a click meant cannot be read from the emitted array alone —
// ticking `db:identity` and unticking `db:read` both arrive as
// `['traces:read', 'db:identity']`. So the previous selection is what
// distinguishes them: a capability that just appeared pulls its grant in, and a
// grant that just disappeared takes its capability with it.
//
// Re-adding the grant and stopping there would leave that second click doing
// nothing visible at all, which reads as a broken checkbox.
const onScopesChange = (scopes) => {
  const previous = form.value.scopes;
  let next = [...scopes];

  for (const { id, requires } of SCOPES) {
    if (!requires) { continue }

    if (next.includes(id) && !previous.includes(id) && !next.includes(requires)) {
      next.push(requires);
    }
    if (previous.includes(requires) && !next.includes(requires)) {
      next = next.filter((entry) => entry !== id);
    }
  }

  form.value.scopes = orderedScopes(next);
};

const create = async() => {
  if (!form.value.label.trim() || isSaving.value) { return }
  const scope = form.value.scopes;
  if (!scope.length) {
    useToast.error(useT('cliToken.scope.required'));
    return;
  }
  isSaving.value = true;
  try {
    minted.value = await useApi.post('/cli-tokens', {
      body: { label: form.value.label.trim(), scope, ttlDays: form.value.ttlDays },
    });
  } catch (error) {
    useToast.error(apiErrorMessage(error, 'cliToken.toast.createError'));
  } finally {
    isSaving.value = false;
  }
};

// Label and access only. The secret is untouched, so the machines holding this
// token keep working with whatever access it now has.
const update = async() => {
  const token = editTarget.value;
  if (!token || !form.value.label.trim() || isSaving.value) { return }
  const scope = form.value.scopes;
  if (!scope.length) {
    useToast.error(useT('cliToken.scope.required'));
    return;
  }
  isSaving.value = true;
  try {
    await useApi.patch(`/cli-tokens/${token._id}`, {
      body: { label: form.value.label.trim(), scope },
    });
    useToast(useT('cliToken.toast.updated'));
    onFormClose();
    fetchTokens();
  } catch (error) {
    useToast.error(apiErrorMessage(error, 'cliToken.toast.updateError'));
  } finally {
    isSaving.value = false;
  }
};

const submit = () => (isEditing.value ? update() : create());

const copyMinted = async() => {
  if (!minted.value?.token) { return }
  try {
    await navigator.clipboard.writeText(minted.value.token);
    copied.value = true;
    setTimeout(() => { copied.value = false }, 1500);
  } catch (error) {
    useToast.error(useT('cliToken.toast.clipboardError'));
  }
};

const revoke = (token) => {
  revokeTarget.value = token;
  revokeOpen.value = true;
};

const onRevokeClose = (closeFn) => {
  revokeOpen.value = false;
  if (typeof closeFn === 'function') { closeFn() }
  revokeTarget.value = null;
};

const confirmRevoke = async() => {
  const token = revokeTarget.value;
  if (!token || isRevoking.value) { return }
  isRevoking.value = true;
  try {
    await useApi.delete(`/cli-tokens/${token._id}`);
    useToast(useT('cliToken.toast.revoked'));
    onRevokeClose();
    fetchTokens();
  } catch (error) {
    useToast.error(useT('cliToken.toast.revokeError'));
  } finally {
    isRevoking.value = false;
  }
};

const isInactive = (token) => Boolean(token.revoked || token.expired);

const isExpiringSoon = (token) => !isInactive(token) &&
  Boolean(token.expiresAt) &&
  new Date(token.expiresAt).getTime() - Date.now() < EXPIRING_SOON_MS;

const rowClassName = ({ row }) => (isInactive(row) ? 'cli-token-row--inactive' : '');

const statusLabel = (token) => {
  if (token.revoked) { return useT('cliToken.status.revoked') }
  if (token.expired) { return useT('cliToken.status.expired') }
  if (isExpiringSoon(token)) { return useT('cliToken.status.expiringSoon') }
  return useT('cliToken.status.active');
};

// Expired is terminal, not actionable — warning is reserved for the tokens you
// can still do something about.
const statusType = (token) => {
  if (isInactive(token)) { return 'neutral' }
  if (isExpiringSoon(token)) { return 'warning' }
  return 'success';
};

// The date the badge is talking about: when it died, or when it will.
const statusDate = (token) => (token.revoked ? token.revokedAt : token.expiresAt);

const statusDetail = (token) => {
  if (isInactive(token) || isExpiringSoon(token)) { return formatRelative(statusDate(token)) }
  return useT('cliToken.expiresIn', { time: formatRelative(token.expiresAt) });
};

// Badges read down the column, so two tokens with the same access have to look
// the same. The API stores `scope` in the order it was submitted — i.e. the
// order the boxes happened to be ticked — so the canonical SCOPES order is
// applied at render. Anything unrecognised sorts last rather than vanishing.
const orderedScopes = (scopes) => [...(scopes || [])].sort((one, other) => {
  const rank = (id) => {
    const index = SCOPES.findIndex((scope) => scope.id === id);
    return index === -1 ? SCOPES.length : index;
  };
  return rank(one) - rank(other);
});

const scopeLabel = (scopeName) => (SCOPE_BY_ID[scopeName] ? useT(SCOPE_BY_ID[scopeName].short) : scopeName);

const scopeTooltip = (scopeName) => (SCOPE_BY_ID[scopeName] ? useT(SCOPE_BY_ID[scopeName].full) : scopeName);

const scopeBadgeType = (scopeName) => SCOPE_BY_ID[scopeName]?.badgeType || 'neutral';

const pad = (n) => String(n).padStart(2, '0');
const formatDate = (value) => {
  if (!value) { return '—' }
  const date = new Date(value);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatRelative = (value) => useFormatRelative(value);

// Lifecycle
onMounted(fetchTokens);
</script>

<style scoped>
/* Revoked and expired tokens stay listed as audit context, but they shouldn't
   read as loudly as the tokens that still grant access. */
:deep(.el-table__row.cli-token-row--inactive) .cell {
  opacity: 0.6;
}
</style>
